import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
    Heart, 
    ClipboardList, 
    Plus, 
    Trash2, 
    Edit3, 
    ShoppingCart, 
    Search, 
    X, 
    ChevronDown, 
    ChevronUp, 
    Calendar,
    Loader2
} from 'lucide-react';
import { 
    collection, 
    doc, 
    onSnapshot, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where, 
    limit, 
    getDocs
} from 'firebase/firestore';
import { execute, field, stringContains, toLower } from 'firebase/firestore/pipelines';
import { db, auth } from '../../utils/firebase';
import ProductCard from '../ProductCard';
import { toast } from 'sonner';

export default function MyLists({ myList, handleAddToCart, handleToggleMyList }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'easy_orders';
    const [activeTab, setActiveTab] = useState(initialTab);

    const [userId, setUserId] = useState(null);

    // Easy Orders State
    const [easyOrders, setEasyOrders] = useState([]);
    const [loadingEasyOrders, setLoadingEasyOrders] = useState(true);

    // Create Modal State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [createDraftItems, setCreateDraftItems] = useState([]);
    const [isCreatingList, setIsCreatingList] = useState(false);

    // Edit Modal State
    const [editingList, setEditingList] = useState(null);
    const [editingListName, setEditingListName] = useState('');
    const [editingItems, setEditingItems] = useState([]);
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    // Product Search inside Modal State
    const [modalSearchQuery, setModalSearchQuery] = useState('');
    const [modalSearchResults, setModalSearchResults] = useState([]);
    const [isSearchingProducts, setIsSearchingProducts] = useState(false);

    // Wishlist State
    const [wishlistProducts, setWishlistProducts] = useState([]);
    const [loadingWishlist, setLoadingWishlist] = useState(true);

    // Card Expand State
    const [expandedCardId, setExpandedCardId] = useState(null);

    // Sync tab with URL
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && (tabFromUrl === 'easy_orders' || tabFromUrl === 'wishlist')) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    // Firebase Auth & Realtime Easy Orders Listener
    useEffect(() => {
        let unsubscribeEasyOrders = null;

        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserId(user.uid);

                // Subscribe to users/{uid}/easy_orders
                const easyOrdersRef = collection(db, "users", user.uid, "easy_orders");
                unsubscribeEasyOrders = onSnapshot(easyOrdersRef, (snapshot) => {
                    const list = snapshot.docs.map(docSnap => ({
                        id: docSnap.id,
                        ...docSnap.data()
                    }));
                    setEasyOrders(list);
                    setLoadingEasyOrders(false);
                }, (error) => {
                    console.error("Error listening to easy_orders:", error);
                    setLoadingEasyOrders(false);
                });

                // Fetch Wishlist products
                fetchWishlist(user.uid);
            } else {
                setUserId(null);
                setEasyOrders([]);
                setWishlistProducts([]);
                setLoadingEasyOrders(false);
                setLoadingWishlist(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeEasyOrders) unsubscribeEasyOrders();
        };
    }, []);

    // Fetch Wishlist Items
    const fetchWishlist = async (uid) => {
        setLoadingWishlist(true);
        try {
            const productsRef = collection(db, "products");
            const q = query(productsRef, where("wishlistIds", "array-contains", uid), limit(30));
            const snap = await getDocs(q);
            const batch = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setWishlistProducts(batch);
        } catch (err) {
            console.error("Error fetching wishlist products:", err);
        } finally {
            setLoadingWishlist(false);
        }
    };

    // Modal Product Search (uses Firestore pipeline stringContains or getDocs fallback)
    useEffect(() => {
        const queryStr = modalSearchQuery.trim().toLowerCase();
        if (!queryStr) {
            setModalSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setIsSearchingProducts(true);
            try {
                // Try executing pipeline query
                const snapshot = await execute(
                    db.pipeline()
                        .collection('products')
                        .where(stringContains(toLower(field('productName')), queryStr))
                        .limit(8)
                );
                const results = snapshot.results.map(r => {
                    const data = r.data();
                    const docId = r.id || '';
                    return {
                        product_id: docId,
                        title: data.productName || data.title || '',
                        code: data.productCodeLetters && data.productCode ? `${data.productCodeLetters}${data.productCode}` : (data.code || ''),
                        image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                        price: data.distributorPrice || data.price || 0,
                        rawProduct: { id: docId, ...data }
                    };
                });
                setModalSearchResults(results);
            } catch (e) {
                // Fallback: fetch products
                try {
                    const snap = await getDocs(query(collection(db, 'products'), limit(30)));
                    const filtered = snap.docs
                        .map(d => {
                            const data = d.data();
                            return {
                                product_id: d.id,
                                title: data.productName || data.title || '',
                                code: data.productCodeLetters && data.productCode ? `${data.productCodeLetters}${data.productCode}` : (data.code || ''),
                                image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                                price: data.distributorPrice || data.price || 0,
                                rawProduct: { id: d.id, ...data }
                            };
                        })
                        .filter(item => item.title.toLowerCase().includes(queryStr) || item.code.toLowerCase().includes(queryStr));
                    setModalSearchResults(filtered.slice(0, 8));
                } catch (fallbackErr) {
                    console.error("Error searching products for list:", fallbackErr);
                }
            } finally {
                setIsSearchingProducts(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [modalSearchQuery]);

    // Add product to draft items during creation
    const handleAddProductToDraft = (productItem) => {
        setCreateDraftItems(prev => {
            const idx = prev.findIndex(item => item.product_id === productItem.product_id);
            if (idx > -1) {
                const updated = [...prev];
                updated[idx].quantity += 1;
                return updated;
            } else {
                return [...prev, { ...productItem, quantity: 1 }];
            }
        });
        toast.success(`Added ${productItem.title} to list draft`);
    };

    // Add product to editing items list
    const handleAddProductToEditing = (productItem) => {
        setEditingItems(prev => {
            const idx = prev.findIndex(item => item.product_id === productItem.product_id);
            if (idx > -1) {
                const updated = [...prev];
                updated[idx].quantity += 1;
                return updated;
            } else {
                return [...prev, { ...productItem, quantity: 1 }];
            }
        });
        toast.success(`Added ${productItem.title}`);
    };

    // Create New Easy Order List in Firestore
    const handleCreateListSubmit = async (e) => {
        e.preventDefault();
        if (!newListName.trim()) {
            toast.error("Please enter a list name");
            return;
        }
        if (!userId) {
            toast.error("Please sign in to save lists");
            return;
        }

        setIsCreatingList(true);
        try {
            const easyOrdersRef = collection(db, "users", userId, "easy_orders");
            await addDoc(easyOrdersRef, {
                name: newListName.trim(),
                items: createDraftItems,
                createdAt: new Date().toISOString()
            });

            toast.success(`List "${newListName.trim()}" created successfully!`);
            setShowCreateModal(false);
            setNewListName('');
            setCreateDraftItems([]);
            setModalSearchQuery('');
        } catch (err) {
            console.error("Error creating list:", err);
            toast.error("Failed to create list");
        } finally {
            setIsCreatingList(false);
        }
    };

    // Open Edit Modal
    const handleOpenEditModal = (list) => {
        setEditingList(list);
        setEditingListName(list.name || '');
        setEditingItems(list.items || []);
        setModalSearchQuery('');
        setModalSearchResults([]);
    };

    // Save Edited List to Firestore
    const handleSaveEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingList || !userId) return;
        if (!editingListName.trim()) {
            toast.error("List name cannot be empty");
            return;
        }

        setIsSavingEdit(true);
        try {
            const listRef = doc(db, "users", userId, "easy_orders", editingList.id);
            await updateDoc(listRef, {
                name: editingListName.trim(),
                items: editingItems,
                updatedAt: new Date().toISOString()
            });

            toast.success("List updated successfully!");
            setEditingList(null);
        } catch (err) {
            console.error("Error saving list:", err);
            toast.error("Failed to update list");
        } finally {
            setIsSavingEdit(false);
        }
    };

    // Delete Easy Order List
    const handleDeleteList = async (listId, listName) => {
        if (!userId) return;
        if (!window.confirm(`Are you sure you want to delete "${listName}"?`)) return;

        try {
            const listRef = doc(db, "users", userId, "easy_orders", listId);
            await deleteDoc(listRef);
            toast.success(`List "${listName}" deleted`);
        } catch (err) {
            console.error("Error deleting list:", err);
            toast.error("Failed to delete list");
        }
    };

    // Add all items in a list to Cart
    const handleAddAllToCart = (items) => {
        if (!items || items.length === 0) {
            toast.error("This list is empty!");
            return;
        }

        let addedCount = 0;
        items.forEach(item => {
            const product = item.rawProduct || {
                id: item.product_id,
                title: item.title,
                image: item.image,
                code: item.code,
                price: item.price
            };
            handleAddToCart(product, Number(item.quantity) || 1);
            addedCount++;
        });

        toast.success(`Added ${addedCount} ${addedCount === 1 ? 'item' : 'items'} to cart!`);
    };

    // Compute total estimated price of a list
    const computeListTotal = (items) => {
        if (!items || !items.length) return 0;
        return items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] pb-24 pt-8">
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">

                {/* ================= HEADER & TABS ================= */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 bg-white p-6 sm:p-8 rounded-3xl border border-[#E6E6E6] shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-[#154734] text-white rounded-2xl flex items-center justify-center shadow-lg shadow-[#154734]/20 shrink-0">
                            <ClipboardList className="w-7 h-7" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-black text-[#154734] tracking-tight">My Saved Lists</h1>
                            <p className="text-sm text-slate-500 font-semibold mt-1">
                                Create, manage and re-order your restock template lists
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Tab Switcher */}
                        <div className="flex items-center p-1.5 bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl">
                            <button
                                onClick={() => handleTabChange('easy_orders')}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer border-none ${
                                    activeTab === 'easy_orders'
                                        ? 'bg-[#154734] text-white shadow-md'
                                        : 'bg-transparent text-slate-600 hover:text-[#154734]'
                                }`}
                            >
                                <ClipboardList className="w-4 h-4" />
                                <span>Easy Order Lists ({easyOrders.length})</span>
                            </button>

                            <button
                                onClick={() => handleTabChange('wishlist')}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer border-none ${
                                    activeTab === 'wishlist'
                                        ? 'bg-[#154734] text-white shadow-md'
                                        : 'bg-transparent text-slate-600 hover:text-[#154734]'
                                }`}
                            >
                                <Heart className="w-4 h-4 text-red-400 fill-red-400" />
                                <span>Favorites ({myList.length})</span>
                            </button>
                        </div>

                        {/* Create List Button */}
                        {activeTab === 'easy_orders' && userId && (
                            <button
                                onClick={() => {
                                    setShowCreateModal(true);
                                    setNewListName('');
                                    setCreateDraftItems([]);
                                    setModalSearchQuery('');
                                }}
                                className="flex items-center gap-2 bg-[#FF7A00] hover:bg-[#E66E00] text-white font-extrabold px-5 py-3 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer border-none text-xs sm:text-sm"
                            >
                                <Plus className="w-4 h-4 stroke-[3]" />
                                <span>Create New List</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* ================= TAB 1: EASY ORDER LISTS ================= */}
                {activeTab === 'easy_orders' && (
                    <div>
                        {!userId ? (
                            <div className="bg-white rounded-3xl border border-[#E6E6E6] p-12 text-center max-w-lg mx-auto shadow-sm my-12">
                                <div className="w-16 h-16 bg-[#FFF7EC] text-[#154734] rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <ClipboardList className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-black text-[#154734]">Please Sign In</h3>
                                <p className="text-slate-500 text-sm font-medium mt-2 mb-6">
                                    Sign in to view your saved Easy Order restock lists and templates.
                                </p>
                                <button
                                    onClick={() => window.dispatchEvent(new Event('open-auth'))}
                                    className="bg-[#154734] text-white font-extrabold px-6 py-3 rounded-2xl hover:bg-[#0A2E22] transition-all cursor-pointer border-none text-sm shadow-md"
                                >
                                    Sign In / Register
                                </button>
                            </div>
                        ) : loadingEasyOrders ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <Loader2 className="w-10 h-10 text-[#154734] animate-spin mb-3" />
                                <p className="text-slate-500 font-bold text-sm">Loading your restock lists...</p>
                            </div>
                        ) : easyOrders.length === 0 ? (
                            <div className="bg-white rounded-3xl border border-[#E6E6E6] p-12 text-center max-w-xl mx-auto shadow-sm my-8">
                                <div className="w-20 h-20 bg-[#FFF7EC] text-[#154734] rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-inner">
                                    <ClipboardList className="w-10 h-10" />
                                </div>
                                <h3 className="text-2xl font-black text-[#154734]">No Saved Easy Order Lists Yet</h3>
                                <p className="text-slate-500 text-sm font-semibold mt-2 mb-6 max-w-md mx-auto">
                                    Create saved lists for your routine restaurant and kitchen supplies to re-order in a single click anytime!
                                </p>
                                <button
                                    onClick={() => {
                                        setShowCreateModal(true);
                                        setNewListName('');
                                        setCreateDraftItems([]);
                                        setModalSearchQuery('');
                                    }}
                                    className="bg-[#154734] hover:bg-[#0A2E22] text-white font-black px-7 py-3.5 rounded-2xl transition-all cursor-pointer border-none text-sm shadow-lg shadow-[#154734]/20 hover:-translate-y-0.5"
                                >
                                    + Create Your First Restock List
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {easyOrders.map((list) => {
                                    const itemCount = list.items?.length || 0;
                                    const totalEst = computeListTotal(list.items);
                                    const isExpanded = expandedCardId === list.id;

                                    return (
                                        <div
                                            key={list.id}
                                            className="bg-white rounded-3xl border border-[#E6E6E6] p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                                        >
                                            <div>
                                                {/* Card Top Row */}
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <div>
                                                        <h3 className="text-lg font-black text-[#154734] leading-tight m-0">
                                                            {list.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400 font-semibold">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            <span>Created {list.createdAt ? new Date(list.createdAt).toLocaleDateString() : 'Recently'}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <button
                                                            onClick={() => handleOpenEditModal(list)}
                                                            className="w-8 h-8 rounded-xl bg-[#FAF9F6] text-slate-600 hover:bg-[#154734] hover:text-white transition-all flex items-center justify-center cursor-pointer border-none"
                                                            title="Edit List"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteList(list.id, list.name)}
                                                            className="w-8 h-8 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all flex items-center justify-center cursor-pointer border-none"
                                                            title="Delete List"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Summary Stats */}
                                                <div className="flex items-center justify-between bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl p-3 my-4">
                                                    <div>
                                                        <span className="text-[11px] font-bold text-slate-400 block uppercase">Total Products</span>
                                                        <span className="text-sm font-black text-[#154734]">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                                                    </div>
                                                    {totalEst > 0 && (
                                                        <div className="text-right">
                                                            <span className="text-[11px] font-bold text-slate-400 block uppercase">Est. Value</span>
                                                            <span className="text-sm font-black text-[#154734]">₹{totalEst.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Item List Preview / Expander */}
                                                {itemCount > 0 ? (
                                                    <div className="mb-4">
                                                        <button
                                                            onClick={() => setExpandedCardId(isExpanded ? null : list.id)}
                                                            className="flex items-center justify-between w-full text-xs font-extrabold text-[#154734] bg-transparent border-none cursor-pointer py-1.5"
                                                        >
                                                            <span>{isExpanded ? 'Hide Items' : `View Items (${itemCount})`}</span>
                                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                        </button>

                                                        {isExpanded && (
                                                            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto divide-y divide-[#F5F5F5] pr-1">
                                                                {list.items.map((item, idx) => (
                                                                    <div key={idx} className="pt-2 flex items-center justify-between gap-3 text-xs">
                                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                                            <img
                                                                                src={item.image || '/product-placeholder.png'}
                                                                                alt={item.title}
                                                                                className="w-8 h-8 object-contain rounded-lg border border-[#E6E6E6] bg-white p-0.5 shrink-0"
                                                                            />
                                                                            <div className="min-w-0">
                                                                                <div className="font-bold text-slate-800 truncate">{item.title}</div>
                                                                                {item.code && <div className="text-[10px] text-slate-400 font-semibold">{item.code}</div>}
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-right shrink-0">
                                                                            <span className="font-black text-[#154734]">Qty: {item.quantity}</span>
                                                                            {item.price > 0 && <div className="text-[10px] text-slate-500 font-bold">₹{item.price}</div>}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic mb-4">No items added to this list yet.</p>
                                                )}
                                            </div>

                                            {/* Action Button */}
                                            <button
                                                onClick={() => handleAddAllToCart(list.items)}
                                                disabled={itemCount === 0}
                                                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-black text-xs sm:text-sm transition-all border-none ${
                                                    itemCount > 0
                                                        ? 'bg-[#154734] hover:bg-[#0A2E22] text-white cursor-pointer shadow-md hover:-translate-y-0.5'
                                                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                }`}
                                            >
                                                <ShoppingCart className="w-4 h-4" />
                                                <span>Add All Items to Cart</span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ================= TAB 2: WISHLIST / FAVORITES ================= */}
                {activeTab === 'wishlist' && (
                    <div>
                        {!userId ? (
                            <div className="bg-white rounded-3xl border border-[#E6E6E6] p-12 text-center max-w-lg mx-auto shadow-sm my-12">
                                <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Heart className="w-8 h-8 fill-red-500" />
                                </div>
                                <h3 className="text-xl font-black text-[#154734]">Please Sign In</h3>
                                <p className="text-slate-500 text-sm font-medium mt-2 mb-6">
                                    Sign in to view your saved favorite products.
                                </p>
                                <button
                                    onClick={() => window.dispatchEvent(new Event('open-auth'))}
                                    className="bg-[#154734] text-white font-extrabold px-6 py-3 rounded-2xl hover:bg-[#0A2E22] transition-all cursor-pointer border-none text-sm shadow-md"
                                >
                                    Sign In / Register
                                </button>
                            </div>
                        ) : loadingWishlist ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <Loader2 className="w-10 h-10 text-[#154734] animate-spin mb-3" />
                                <p className="text-slate-500 font-bold text-sm">Loading your favorites...</p>
                            </div>
                        ) : wishlistProducts.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                {wishlistProducts.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onAddToCart={handleAddToCart}
                                        isLiked={true}
                                        onToggleMyList={() => {
                                            setWishlistProducts(prev => prev.filter(p => p.id !== product.id));
                                            if (handleToggleMyList) handleToggleMyList(product.id);
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl border border-[#E6E6E6] p-12 text-center max-w-lg mx-auto shadow-sm my-12">
                                <div className="w-16 h-16 bg-red-50 text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Heart className="w-8 h-8 fill-red-400" />
                                </div>
                                <h3 className="text-xl font-black text-[#154734]">Your Favorites List is Empty</h3>
                                <p className="text-slate-500 text-sm font-medium mt-2 mb-6">
                                    Click the heart icon on any product to quickly save it to your favorites.
                                </p>
                                <Link to="/products" className="no-underline">
                                    <button className="bg-[#154734] text-white font-extrabold px-6 py-3 rounded-2xl hover:bg-[#0A2E22] transition-all cursor-pointer border-none text-sm shadow-md">
                                        Browse Products
                                    </button>
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {/* ================= CREATE LIST MODAL ================= */}
                {showCreateModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-[#E6E6E6] max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#FFF7EC] text-[#154734] rounded-xl flex items-center justify-center font-bold">
                                        <Plus className="w-5 h-5 stroke-[3]" />
                                    </div>
                                    <h2 className="text-xl font-black text-[#154734]">Create New Restock List</h2>
                                </div>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="w-8 h-8 rounded-full bg-[#FAF9F6] text-slate-400 hover:text-slate-700 flex items-center justify-center border-none cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleCreateListSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                                        List Name *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Weekly Produce & Bar Setup"
                                        value={newListName}
                                        onChange={(e) => setNewListName(e.target.value)}
                                        required
                                        className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] focus:ring-2 focus:ring-[#154734]/20 transition-all"
                                    />
                                </div>

                                {/* Add Products Search */}
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                                        Add Products (Optional)
                                    </label>
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-3.5" />
                                        <input
                                            type="text"
                                            placeholder="Search products to add..."
                                            value={modalSearchQuery}
                                            onChange={(e) => setModalSearchQuery(e.target.value)}
                                            className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                        />
                                    </div>

                                    {/* Search Results Dropdown */}
                                    {isSearchingProducts ? (
                                        <div className="p-3 text-center text-xs text-slate-400 font-bold">Searching...</div>
                                    ) : modalSearchResults.length > 0 ? (
                                        <div className="mt-2 bg-white border border-[#E6E6E6] rounded-2xl max-h-48 overflow-y-auto divide-y divide-[#F5F5F5] shadow-sm">
                                            {modalSearchResults.map((res) => (
                                                <div key={res.product_id} className="p-2.5 flex items-center justify-between hover:bg-[#FAF9F6] transition">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <img src={res.image} alt={res.title} className="w-8 h-8 object-contain rounded border border-[#E6E6E6] p-0.5" />
                                                        <div className="min-w-0">
                                                            <div className="text-xs font-bold text-slate-800 truncate">{res.title}</div>
                                                            <div className="text-[10px] text-slate-400 font-semibold">{res.code}</div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddProductToDraft(res)}
                                                        className="px-3 py-1 rounded-xl bg-[#154734] text-white text-xs font-bold border-none cursor-pointer hover:bg-[#0A2E22] transition shrink-0"
                                                    >
                                                        + Add
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>

                                {/* Selected Draft Items */}
                                {createDraftItems.length > 0 && (
                                    <div className="bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl p-4">
                                        <h4 className="text-xs font-black text-[#154734] uppercase tracking-wider mb-3">
                                            Items in List ({createDraftItems.length})
                                        </h4>
                                        <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                                            {createDraftItems.map((item, idx) => (
                                                <div key={idx} className="flex items-center justify-between gap-3 bg-white p-2.5 rounded-xl border border-[#E6E6E6]">
                                                    <span className="text-xs font-bold text-slate-800 truncate">{item.title}</span>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => {
                                                                const val = Math.max(1, parseInt(e.target.value) || 1);
                                                                setCreateDraftItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: val } : it));
                                                            }}
                                                            className="w-14 bg-[#FAF9F6] border border-[#E6E6E6] rounded-lg px-2 py-1 text-center text-xs font-black"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setCreateDraftItems(prev => prev.filter((_, i) => i !== idx))}
                                                            className="text-red-400 hover:text-red-600 bg-transparent border-none cursor-pointer"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E6E6E6]">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-5 py-2.5 rounded-2xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-transparent border-none cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isCreatingList}
                                        className="bg-[#154734] hover:bg-[#0A2E22] text-white font-extrabold px-6 py-2.5 rounded-2xl text-xs sm:text-sm transition-all border-none cursor-pointer shadow-md flex items-center gap-2"
                                    >
                                        {isCreatingList && <Loader2 className="w-4 h-4 animate-spin" />}
                                        <span>Save List</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= EDIT LIST MODAL ================= */}
                {editingList && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border border-[#E6E6E6] max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#FFF7EC] text-[#154734] rounded-xl flex items-center justify-center font-bold">
                                        <Edit3 className="w-5 h-5" />
                                    </div>
                                    <h2 className="text-xl font-black text-[#154734]">Edit Restock List</h2>
                                </div>
                                <button
                                    onClick={() => setEditingList(null)}
                                    className="w-8 h-8 rounded-full bg-[#FAF9F6] text-slate-400 hover:text-slate-700 flex items-center justify-center border-none cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSaveEditSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                                        List Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={editingListName}
                                        onChange={(e) => setEditingListName(e.target.value)}
                                        required
                                        className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                    />
                                </div>

                                {/* Add More Products Search */}
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                                        Add More Products
                                    </label>
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-3.5" />
                                        <input
                                            type="text"
                                            placeholder="Search products to add to this list..."
                                            value={modalSearchQuery}
                                            onChange={(e) => setModalSearchQuery(e.target.value)}
                                            className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                        />
                                    </div>

                                    {/* Search Results Dropdown */}
                                    {isSearchingProducts ? (
                                        <div className="p-3 text-center text-xs text-slate-400 font-bold">Searching...</div>
                                    ) : modalSearchResults.length > 0 ? (
                                        <div className="mt-2 bg-white border border-[#E6E6E6] rounded-2xl max-h-48 overflow-y-auto divide-y divide-[#F5F5F5] shadow-sm">
                                            {modalSearchResults.map((res) => (
                                                <div key={res.product_id} className="p-2.5 flex items-center justify-between hover:bg-[#FAF9F6] transition">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <img src={res.image} alt={res.title} className="w-8 h-8 object-contain rounded border border-[#E6E6E6] p-0.5" />
                                                        <div className="min-w-0">
                                                            <div className="text-xs font-bold text-slate-800 truncate">{res.title}</div>
                                                            <div className="text-[10px] text-slate-400 font-semibold">{res.code}</div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddProductToEditing(res)}
                                                        className="px-3 py-1 rounded-xl bg-[#154734] text-white text-xs font-bold border-none cursor-pointer hover:bg-[#0A2E22] transition shrink-0"
                                                    >
                                                        + Add
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>

                                {/* List Items Table */}
                                <div>
                                    <h4 className="text-xs font-black text-[#154734] uppercase tracking-wider mb-3">
                                        Products in List ({editingItems.length})
                                    </h4>

                                    {editingItems.length === 0 ? (
                                        <p className="text-xs text-slate-400 italic">No products in this list. Use the search box above to add items.</p>
                                    ) : (
                                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                            {editingItems.map((item, idx) => (
                                                <div key={idx} className="flex items-center justify-between gap-3 bg-[#FAF9F6] p-3 rounded-2xl border border-[#E6E6E6]">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <img src={item.image || '/product-placeholder.png'} alt={item.title} className="w-9 h-9 object-contain rounded-xl border border-[#E6E6E6] bg-white p-0.5 shrink-0" />
                                                        <div className="min-w-0">
                                                            <div className="text-xs font-bold text-slate-800 truncate">{item.title}</div>
                                                            {item.code && <div className="text-[10px] text-slate-400 font-semibold">{item.code}</div>}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[11px] font-bold text-slate-400">Qty:</span>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => {
                                                                    const val = Math.max(1, parseInt(e.target.value) || 1);
                                                                    setEditingItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: val } : it));
                                                                }}
                                                                className="w-16 bg-white border border-[#E6E6E6] rounded-xl px-2 py-1 text-center text-xs font-black text-[#154734]"
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingItems(prev => prev.filter((_, i) => i !== idx))}
                                                            className="w-7 h-7 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition flex items-center justify-center border-none cursor-pointer"
                                                            title="Remove Item"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E6E6E6]">
                                    <button
                                        type="button"
                                        onClick={() => setEditingList(null)}
                                        className="px-5 py-2.5 rounded-2xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-transparent border-none cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSavingEdit}
                                        className="bg-[#154734] hover:bg-[#0A2E22] text-white font-extrabold px-6 py-2.5 rounded-2xl text-xs sm:text-sm transition-all border-none cursor-pointer shadow-md flex items-center gap-2"
                                    >
                                        {isSavingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
